export type TokenListener = (token: string | null) => void;

/**
 * Minimal bridge to expose the current Keycloak access token outside React.
 * Consumers can subscribe to token changes or fetch the latest value on demand.
 */
export class TokenBridge {
  private currentToken: string | null = null;
  private listeners = new Set<TokenListener>();

  /**
   * Update the tracked token and notify subscribers when it changes.
   */
  setToken(token: string | null): void {
    if (this.currentToken === token) {
      return;
    }
    this.currentToken = token;
    for (const listener of this.listeners) {
      try {
        listener(token);
      } catch (error) {
        // Swallow listener errors so a single consumer cannot break updates.
        console.error('[TokenBridge] Listener threw error:', error);
      }
    }
  }

  /**
   * Read the most recent token value.
   */
  getToken(): string | null {
    return this.currentToken;
  }

  /**
   * Convenience helper for async callers.
   */
  async getTokenAsync(): Promise<string | null> {
    return this.currentToken;
  }

  /**
   * Subscribe to token changes. Returns an unsubscribe callback.
   */
  subscribe(listener: TokenListener): () => void {
    this.listeners.add(listener);
    // Provide the current value immediately
    listener(this.currentToken);
    return () => {
      this.listeners.delete(listener);
    };
  }
}

/**
 * Factory helper to create an isolated token bridge.
 */
export const createTokenBridge = (): TokenBridge => new TokenBridge();

/**
 * Default shared bridge for simple scenarios.
 */
export const defaultTokenBridge = createTokenBridge();
